import React, { useState } from 'react';
import axios from 'axios';
import { X, Eye, FileCheck, AlertCircle, FileText, Settings, Paperclip, Briefcase } from 'lucide-react';
import AssetAttachments from './AssetAttachments';

type TabType = 'basic' | 'config' | 'assets' | 'task';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface ReviewItemFormProps {
  stepId: number;
  stepTypeId: number;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

interface Asset {
  id: number;
  asset_type: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  r2_key?: string;
  review_status: 'pending' | 'approved' | 'rejected' | 'revision_requested';
  reviewer_feedback?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  metadata?: any;
  created_at: string;
}

const ReviewItemForm: React.FC<ReviewItemFormProps> = ({
  stepId,
  stepTypeId,
  onSubmit,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('basic');

  const [review, setReview] = useState({
    name: '',
    description: '',
    status: 'todo',
    priority: 'high',
    assigned_to: '',
    assigned_to_type: 'client',
    due_date: '',
    estimated_hours: 1,
    // Review-specific fields
    review_type: 'content', // content, design, legal, technical, final
    approval_required_from: 'client', // client, team_lead, legal, stakeholder
    reviewers: [] as string[],
    approval_criteria: '',
    review_deadline: '',
    requires_all_assets_approved: true,
    auto_complete_when_approved: true,
    notes: '',
    escalation_contact: ''
  });

  const handleUploadAsset = async (file: File, assetType: string) => {
    // Temporary mock - will be replaced with actual R2 upload
    const mockAsset: Asset = {
      id: Date.now(),
      asset_type: assetType,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      review_status: 'pending',
      created_at: new Date().toISOString()
    };
    setAssets([...assets, mockAsset]);
  };

  const handleDeleteAsset = async (assetId: number) => {
    setAssets(assets.filter(a => a.id !== assetId));
  };

  const handleUpdateAssetStatus = async (
    assetId: number,
    status: string,
    feedback?: string
  ) => {
    setAssets(assets.map(a =>
      a.id === assetId
        ? {
            ...a,
            review_status: status as any,
            reviewer_feedback: feedback,
            reviewed_at: new Date().toISOString()
          }
        : a
    ));
  };

  const handlePreviewAsset = (asset: Asset) => {
    setPreviewAsset(asset);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const config = {
        type: 'review',
        review_type: review.review_type,
        approval_required_from: review.approval_required_from,
        reviewers: review.reviewers,
        approval_criteria: review.approval_criteria,
        review_deadline: review.review_deadline,
        requires_all_assets_approved: review.requires_all_assets_approved,
        auto_complete_when_approved: review.auto_complete_when_approved,
        notes: review.notes,
        escalation_contact: review.escalation_contact,
        attached_assets: assets.map(a => ({
          id: a.id,
          asset_type: a.asset_type,
          file_name: a.file_name,
          review_status: a.review_status
        }))
      };

      const payload = {
        step_type_id: stepTypeId,
        name: review.name,
        description: review.description,
        status: review.status,
        priority: review.priority,
        assigned_to: review.assigned_to || null,
        assigned_to_type: review.assigned_to_type,
        due_date: review.due_date || null,
        estimated_hours: review.estimated_hours,
        config: JSON.stringify(config)
      };

      await onSubmit(payload);

      // TODO: After item is created, upload assets to R2 and link to project_item_assets table
    } catch (error) {
      console.error('Failed to create review item:', error);
      alert('Failed to create review item');
    } finally {
      setLoading(false);
    }
  };

  const allAssetsApproved = assets.length > 0 && assets.every(a => a.review_status === 'approved');
  const anyAssetsRejected = assets.some(a => a.review_status === 'rejected');

  const tabs = [
    { id: 'basic' as TabType, label: 'Basic Information', icon: <FileText className="w-4 h-4" /> },
    { id: 'config' as TabType, label: 'Review Configuration', icon: <Settings className="w-4 h-4" /> },
    { id: 'assets' as TabType, label: 'Asset Attachments', icon: <Paperclip className="w-4 h-4" />, badge: assets.length },
    { id: 'task' as TabType, label: 'Task Management', icon: <Briefcase className="w-4 h-4" /> }
  ];

  const isTabValid = (tabId: TabType) => {
    if (tabId === 'basic') return review.name.trim() !== '';
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create Review Task</h2>
            <p className="text-sm text-gray-500 mt-1">
              Create a review task with asset attachments
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50 px-6">
          <div className="flex gap-2 -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`
                    ml-1 px-2 py-0.5 rounded-full text-xs font-medium
                    ${activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-200 text-gray-600'
                    }
                  `}>
                    {tab.badge}
                  </span>
                )}
                {!isTabValid(tab.id) && (
                  <span className="w-2 h-2 bg-red-500 rounded-full" title="Required fields missing"></span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={review.name}
                    onChange={(e) => setReview({ ...review, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Review Q4 Marketing Assets"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={review.description}
                    onChange={(e) => setReview({ ...review, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What needs to be reviewed?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review Type
                  </label>
                  <select
                    value={review.review_type}
                    onChange={(e) => setReview({ ...review, review_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="content">Content Review</option>
                    <option value="design">Design Review</option>
                    <option value="legal">Legal Review</option>
                    <option value="technical">Technical Review</option>
                    <option value="final">Final Approval</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Required From
                  </label>
                  <select
                    value={review.approval_required_from}
                    onChange={(e) => setReview({ ...review, approval_required_from: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="client">Client</option>
                    <option value="team_lead">Team Lead</option>
                    <option value="legal">Legal Team</option>
                    <option value="stakeholder">Stakeholder</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Review Configuration Tab */}
          {activeTab === 'config' && (
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Criteria
                  </label>
                  <textarea
                    value={review.approval_criteria}
                    onChange={(e) => setReview({ ...review, approval_criteria: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="What criteria must be met for approval?"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review Deadline
                    </label>
                    <input
                      type="date"
                      value={review.review_deadline}
                      onChange={(e) => setReview({ ...review, review_deadline: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Escalation Contact
                    </label>
                    <input
                      type="text"
                      value={review.escalation_contact}
                      onChange={(e) => setReview({ ...review, escalation_contact: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Who to contact if issues arise"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={review.requires_all_assets_approved}
                      onChange={(e) => setReview({ ...review, requires_all_assets_approved: e.target.checked })}
                      className="mr-2 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Require all assets to be approved before task completion
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={review.auto_complete_when_approved}
                      onChange={(e) => setReview({ ...review, auto_complete_when_approved: e.target.checked })}
                      className="mr-2 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Auto-complete task when all assets are approved
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={review.notes}
                    onChange={(e) => setReview({ ...review, notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Additional notes or instructions..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Asset Attachments Tab */}
          {activeTab === 'assets' && (
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Asset Attachments</h3>
                {allAssetsApproved && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <FileCheck className="w-4 h-4 mr-1" />
                    All Approved
                  </span>
                )}
                {anyAssetsRejected && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Has Rejections
                  </span>
                )}
              </div>

              <AssetAttachments
                assets={assets}
                onUpload={handleUploadAsset}
                onDelete={handleDeleteAsset}
                onPreview={handlePreviewAsset}
                onUpdateStatus={handleUpdateAssetStatus}
                allowUpload={true}
                allowDelete={true}
                allowReview={false}
              />
            </div>
          )}

          {/* Task Management Tab */}
          {activeTab === 'task' && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Management</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={review.status}
                    onChange={(e) => setReview({ ...review, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="waiting">Waiting</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                    <option value="skipped">Skipped</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={review.priority}
                    onChange={(e) => setReview({ ...review, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned To Type
                  </label>
                  <select
                    value={review.assigned_to_type}
                    onChange={(e) => setReview({ ...review, assigned_to_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  >
                    <option value="team">Team Member</option>
                    <option value="client">Client</option>
                    <option value="external">External</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned To
                  </label>
                  <input
                    type="text"
                    value={review.assigned_to}
                    onChange={(e) => setReview({ ...review, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    placeholder="Name or email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={review.due_date}
                    onChange={(e) => setReview({ ...review, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Hours
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={review.estimated_hours}
                    onChange={(e) => setReview({ ...review, estimated_hours: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Review Task'}
          </button>
        </div>
      </div>

      {/* Asset Preview Modal (TODO: Integrate existing preview components) */}
      {previewAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{previewAsset.file_name}</h3>
              <button
                onClick={() => setPreviewAsset(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="text-center text-gray-500">
              <Eye className="w-12 h-12 mx-auto mb-2" />
              <p>Preview for {previewAsset.asset_type} assets</p>
              <p className="text-sm mt-1">Asset preview integration coming soon</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewItemForm;
